import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingBag, Menu, X, Search } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navLinks } from '../mock';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { requestHomeTop } from './ScrollToTop';
import SearchDialog from './SearchDialog';
import { HOME } from '../constants/testIds/home';

function NavAnchor({ href, label, className, onNavigate }) {
  const location = useLocation();
  const navigate = useNavigate();
  const id = href.startsWith('#') ? href.slice(1) : href;

  const handleClick = (e) => {
    e.preventDefault();
    onNavigate?.();

    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.pushState(null, '', href);
      }
      return;
    }

    navigate(`/${href}`);
  };

  return (
    <a href={href} onClick={handleClick} className={className}>
      {label}
    </a>
  );
}

export default function Header({ embedded = false }) {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const { count, setOpen: setCartOpen } = useCart();
  const { isLoggedIn, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const goHomeTop = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === '/') {
      requestHomeTop({ immediate: true });
      if (location.hash) window.history.pushState(null, '', '/');
      return;
    }
    requestHomeTop();
    navigate('/');
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const onScroll = () => setPinned(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock page scroll while the mobile menu is open
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const linkText =
    'text-[10px] sm:text-[11px] tracking-[0.12em] sm:tracking-[0.16em] uppercase text-[#1A2332]/80 hover:text-[#1A2332] transition-colors touch-manipulation whitespace-nowrap';

  const menu = open
    ? createPortal(
        <div className="fixed inset-0 z-[100] lg:hidden" role="dialog" aria-modal="true" aria-label="Mobile menu">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close menu backdrop"
            onClick={() => setOpen(false)}
          />
          <aside className="absolute top-0 left-0 h-full w-[min(20rem,88vw)] bg-[#F5EFE6] shadow-2xl flex flex-col animate-[slideIn_0.25s_ease-out]">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5DCC9] pt-[max(1rem,env(safe-area-inset-top))]">
              <button
                type="button"
                onClick={goHomeTop}
                className="font-serif-display text-lg tracking-[0.2em] text-[#1A2332]"
              >
                RAJPUROHIT
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="p-2 -mr-1 touch-manipulation text-[#1A2332]"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-5 py-2">
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setSearchOpen(true);
                }}
                className="py-3.5 border-b border-[#E5DCC9] uppercase tracking-[0.12em] text-sm text-[#1A2332] touch-manipulation w-full text-left"
              >
                Search
              </button>
              {navLinks.map((l) => (
                <NavAnchor
                  key={l.href}
                  href={l.href}
                  label={l.label}
                  onNavigate={() => setOpen(false)}
                  className="py-3.5 border-b border-[#E5DCC9] uppercase tracking-[0.12em] text-sm text-[#1A2332] touch-manipulation block"
                />
              ))}
              {isLoggedIn ? (
                <Link
                  to={isAdmin ? '/admin' : '/account'}
                  onClick={() => setOpen(false)}
                  className="py-3.5 border-b border-[#E5DCC9] uppercase tracking-[0.12em] text-sm text-[#1A2332] touch-manipulation block"
                >
                  {isAdmin ? 'Admin' : 'My Account'}
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setOpen(false)}
                    className="py-3.5 border-b border-[#E5DCC9] uppercase tracking-[0.12em] text-sm text-[#1A2332] touch-manipulation block"
                  >
                    Login
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setOpen(false)}
                    className="py-3.5 border-b border-[#E5DCC9] uppercase tracking-[0.12em] text-sm text-[#1A2332] touch-manipulation block"
                  >
                    Register
                  </Link>
                </>
              )}
            </nav>
          </aside>
          <style>{`@keyframes slideIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}`}</style>
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <header
        className={`${embedded ? 'relative z-50' : 'sticky top-0 z-50'} border-b transition-[background-color,box-shadow,backdrop-filter,border-color] duration-300 ease-out ${
          pinned
            ? 'bg-[#F5EFE6]/98 backdrop-blur-md shadow-[0_10px_40px_rgba(26,35,50,0.10)] border-[#E5DCC9]/90'
            : 'bg-[#F5EFE6]/95 backdrop-blur border-[#E5DCC9]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 h-14 sm:h-16 lg:h-20 flex items-center justify-between gap-1 sm:gap-2 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:gap-x-6 xl:gap-x-8">
          <div className="flex items-center min-w-[2.5rem]">
            <button
              className="lg:hidden p-2 -ml-1 touch-manipulation"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6 text-[#1A2332]" />
            </button>
            <nav className="hidden lg:flex items-center gap-4 xl:gap-7 text-[11px] xl:text-[13px] uppercase tracking-[0.1em] xl:tracking-[0.12em] text-[#1A2332]/80">
              {navLinks.slice(0, 4).map((l) => (
                <NavAnchor
                  key={l.href}
                  href={l.href}
                  label={l.label}
                  className={`hover:text-[#1A2332] transition-colors relative group whitespace-nowrap${l.label === 'Unisex' ? ' hidden xl:inline' : ''}`}
                />
              ))}
            </nav>
          </div>

          <Link
            to="/"
            onClick={goHomeTop}
            data-testid={HOME.logoLink}
            className="flex flex-col items-center leading-none min-w-0 px-1 lg:px-3 xl:px-4 lg:justify-self-center"
            aria-label="Rajpurohit Perfumes home"
          >
            <span className="font-serif-display text-base sm:text-xl lg:text-[1.45rem] xl:text-[1.65rem] tracking-[0.1em] sm:tracking-[0.2em] lg:tracking-[0.1em] xl:tracking-[0.16em] text-[#1A2332] truncate max-w-[38vw] sm:max-w-none lg:overflow-visible lg:whitespace-nowrap">
              RAJPUROHIT
            </span>
            <span className="text-[7px] sm:text-[9px] lg:text-[10px] xl:text-[11px] tracking-[0.3em] sm:tracking-[0.55em] lg:tracking-[0.32em] xl:tracking-[0.45em] text-[#1A2332]/70 mt-0.5">
              PERFUMES
            </span>
          </Link>

          <div className="flex items-center justify-end gap-1.5 sm:gap-3 lg:gap-3 xl:gap-5">
            <nav className="hidden lg:flex items-center gap-4 xl:gap-6 text-[11px] xl:text-[13px] uppercase tracking-[0.1em] xl:tracking-[0.12em] text-[#1A2332]/80">
              {navLinks.slice(4).map((l) => (
                <NavAnchor
                  key={l.href}
                  href={l.href}
                  label={l.label}
                  className={`hover:text-[#1A2332] transition-colors whitespace-nowrap${l.label !== 'Bundles' ? ' hidden xl:inline' : ''}`}
                />
              ))}
            </nav>

            {isLoggedIn ? (
              <Link to={isAdmin ? '/admin' : '/account'} className={linkText}>
                {isAdmin ? 'Admin' : 'Account'}
              </Link>
            ) : (
              <>
                <Link to="/login" className={linkText}>
                  Login
                </Link>
                <Link to="/register" className={linkText}>
                  Register
                </Link>
              </>
            )}

            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="p-1.5 sm:p-2 hover:opacity-70 transition touch-manipulation"
              aria-label="Search fragrances"
            >
              <Search className="w-5 h-5" />
            </button>

            <button
              onClick={() => setCartOpen(true)}
              className="p-1.5 sm:p-2 hover:opacity-70 transition relative touch-manipulation"
              aria-label="Cart"
            >
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span className="absolute top-0 right-0 bg-[#1A2332] text-[#F5EFE6] text-[10px] w-4 h-4 rounded-full flex items-center justify-center">
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>
      </header>
      {menu}
      <SearchDialog open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}
